import { useEffect, useState, useCallback } from 'react';
import { FileText, Loader as Loader2, Save, ToggleLeft, ToggleRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Clock, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { TERMS_SECTIONS, fetchAcceptanceList, formatAcceptance, type TermsSections, type AcceptanceRow } from '@/lib/b2bTerms';

interface Props {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}

const EMPTY: TermsSections = { sale_terms: '', returns_policy: '', general_notes: '' };

export function B2BTermsSettings({ companyId, showToast }: Props) {
  const [form, setForm] = useState<TermsSections>(EMPTY);
  const [published, setPublished] = useState(false);
  const [version, setVersion] = useState<number | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [list, setList] = useState<AcceptanceRow[] | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'accepted' | 'pending'>('all');

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    const { data, error } = await supabase
      .from('b2b_terms')
      .select('sale_terms, returns_policy, general_notes, is_published, version, updated_at')
      .eq('company_id', companyId)
      .maybeSingle();
    if (error) { setLoadError(error.message); setLoading(false); return; }
    if (data) {
      setForm({ sale_terms: data.sale_terms ?? '', returns_policy: data.returns_policy ?? '', general_notes: data.general_notes ?? '' });
      setPublished(Boolean(data.is_published));
      setVersion(data.version);
      setUpdatedAt(data.updated_at);
      setExists(true);
    } else {
      setForm(EMPTY); setPublished(false); setVersion(null); setUpdatedAt(null); setExists(false);
    }
    setLoading(false);
  }, [companyId]);

  const loadList = useCallback(async () => {
    setListLoading(true); setListError(null);
    try { setList(await fetchAcceptanceList(companyId)); }
    catch (e) { setListError((e as Error).message); }
    setListLoading(false);
  }, [companyId]);

  useEffect(() => { if (companyId) { load(); loadList(); } }, [companyId, load, loadList]);

  const persist = async (nextPublished: boolean) => {
    setSaving(true);
    const payload = {
      company_id: companyId,
      sale_terms: form.sale_terms?.trim() || null,
      returns_policy: form.returns_policy?.trim() || null,
      general_notes: form.general_notes?.trim() || null,
      is_published: nextPublished,
    };
    const { error } = exists
      ? await supabase.from('b2b_terms').update(payload).eq('company_id', companyId)
      : await supabase.from('b2b_terms').insert(payload);
    setSaving(false);
    if (error) { showToast(error.message, false); return false; }
    await load(); await loadList();
    return true;
  };

  const handleSave = async () => {
    if (await persist(published)) showToast('התנאים נשמרו');
  };

  const handleTogglePublish = async () => {
    const next = !published;
    if (await persist(next)) showToast(next ? 'התנאים פורסמו ללקוחות' : 'התנאים הוסתרו מהלקוחות');
  };

  const filtered = (list ?? []).filter(r =>
    filter === 'all' ? true : filter === 'accepted' ? r.accepted_current : !r.accepted_current);
  const acceptedCount = (list ?? []).filter(r => r.accepted_current).length;

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <FileText className="w-5 h-5 text-sky-600" />
        <div>
          <h2 className="text-base font-semibold text-gray-900">הגדרות B2B — תנאים וכללים ללקוחות</h2>
          <p className="text-xs text-gray-500">
            התנאים יופיעו לכל לקוחות ה-B2B. בכרטיס כל לקוח אפשר להגדיר תנאים מיוחדים שיגברו על הכלליים.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 px-6 py-6 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> טוען תנאים…
        </div>
      ) : loadError ? (
        <div className="flex items-center gap-2 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{loadError}
        </div>
      ) : (
        <div className="px-6 py-5 space-y-4">
          <button
            type="button" onClick={handleTogglePublish} disabled={saving}
            className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl border-2 transition-all text-right
              ${published ? 'border-emerald-400 bg-emerald-50 hover:bg-emerald-100' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <div className="flex items-center gap-2 shrink-0">
              {saving ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                : published ? <ToggleRight className="w-6 h-6 text-emerald-500" />
                : <ToggleLeft className="w-6 h-6 text-gray-400" />}
              <span className={`text-sm font-bold ${published ? 'text-emerald-700' : 'text-gray-500'}`}>
                {published ? 'מפורסם' : 'לא מפורסם'}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-right">
              <p className="text-sm font-semibold text-gray-800">פרסם ללקוחות</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {published ? 'הלקוחות רואים את עמוד התנאים ומתבקשים לאשר אותם' : 'הלקוחות לא רואים את התנאים כרגע'}
              </p>
            </div>
          </button>

          {TERMS_SECTIONS.map(s => (
            <div key={s.key} className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">{s.label}</label>
              <textarea
                rows={4} value={form[s.key] ?? ''}
                onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm leading-relaxed
                           focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>
          ))}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-400">
              {version ? `גרסה ${version}` : 'טרם נשמרו תנאים'}
              {updatedAt && ` · עודכן ${new Date(updatedAt).toLocaleDateString('he-IL')}`}
              {' · '}כל שינוי בתוכן יוצר גרסה חדשה ויבקש מהלקוחות לאשר מחדש
            </p>
            <button
              onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium
                         rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              שמור תנאים
            </button>
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-800">אישורי לקוחות</h3>
                {list && <span className="text-xs text-gray-400">{acceptedCount} מתוך {list.length} אישרו את הגרסה הנוכחית</span>}
              </div>
              <div className="flex gap-1">
                {([['all', 'הכל'], ['accepted', 'אישרו'], ['pending', 'טרם אישרו']] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                      ${filter === k ? 'bg-sky-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {listLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> טוען…</div>
            ) : listError ? (
              <p className="text-sm text-red-600">{listError}</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400">אין לקוחות להצגה</p>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-100">
                {filtered.map(r => (
                  <div key={r.client_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">
                        {r.client_name}
                        {r.has_special_terms && (
                          <span className="mr-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] font-medium">תנאים מיוחדים</span>
                        )}
                      </p>
                      {r.accepted_at && (
                        <p className="text-xs text-gray-400">
                          {r.accepted_current ? 'אישר: ' : 'אישור אחרון (גרסה קודמת): '}
                          {formatAcceptance(r.accepted_at, r.accepted_by_type, r.accepted_by_name)}
                        </p>
                      )}
                    </div>
                    {r.accepted_current ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 shrink-0">
                        <CheckCircle2 className="w-4 h-4" /> אישר
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-700 shrink-0">
                        <Clock className="w-4 h-4" /> {r.accepted_at ? 'ממתין לאישור גרסה חדשה' : 'טרם אישר'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
